import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type ScoreBandStage = 'foundational' | 'preparatory' | 'middle' | 'secondary';

export interface BandRow {
  key: string;
  label: string;
  range: string;
  color: string;
  count: number;
  pct: number;
}

interface Note {
  title: string;
  body: string;
  /** 'caution' for a data caveat the reader should weigh, shown in amber. */
  tone?: 'info' | 'caution';
}

interface StageText {
  name: string;
  classes: string;
  score: string;
  examples: string[];
  counted: string;
  notes: Note[];
}

/** Classes III–XI keep one final result per student (studentID is unique per class/year). */
const ONE_RESULT_PER_STUDENT: Note = {
  title: 'Each student is counted once',
  body: 'Every student has exactly one final result record, so these are true student counts, not counts of results.',
};

/**
 * Classes III–XI: one row per student in student_previous_result_details, scored by
 * its final `marks` % (kvsresult SUMMARY_AGG_SELECT). The term never enters that query.
 */
const marksStage = (name: string, classes: string, counted: string, extraNotes: Note[] = []): StageText => ({
  name,
  classes,
  score: "The student's overall marks percentage in their final result for the year.",
  examples: ['95% → Excellent', '72% → Moderate', '45% → Needs Support'],
  counted,
  notes: [
    ...extraNotes,
    {
      title: 'The Term filter does not change these figures',
      body: 'They use each student’s final result for the year, so choosing Term 1 or Term 2 on the dashboard leaves them as they are. Term only matters for the Foundational stage.',
    },
    ONE_RESULT_PER_STUDENT,
    {
      title: 'How they relate to the dashboard chart',
      body: 'Excellent and Needs Support use the same cut-offs as the chart’s Excellent and Below Average groups; Moderate covers Good and Average together.',
    },
  ],
});

/** What each stage's score is and who it counts — the backend rules, in plain words. */
const stageText = (stage: ScoreBandStage, foundationTerm: 1 | 2): StageText => {
  switch (stage) {
    case 'foundational': {
      // utils/foundationStageSummary.ts: per-student rubric mean ÷ 3 × 100, one HPC term.
      const termName = foundationTerm === 1 ? 'Term 1 (Entry)' : 'Term 2 (Exit)';
      return {
        name: 'Foundational Stage',
        classes: 'Balvatika I–III and Class I–II',
        score: `Teachers rate every competency of every student: 1 = Beginner, 2 = Progressing, 3 = Proficient. A student’s score is the average of their ${termName} ratings ÷ 3 × 100.`,
        examples: ['All 1s → 33.3%', 'All 2s → 66.7%', 'All 3s → 100%', 'Average 2.8 → 93.3% → Excellent'],
        counted: `Students with at least one ${termName} rating in the session’s Holistic Progress Card records.`,
        notes: [
          {
            title: 'A different scale from Classes III–XI',
            body: 'The score is an average teacher rating, not exam marks. Above 90% means an average rating above 2.7 out of 3, so these bands are not directly comparable with the marks-based bands of Classes III–XI.',
          },
          {
            title: 'Not the same as the dashboard’s level tiles',
            body: 'The Foundation dashboard’s Proficient / Progressing / Beginner tiles count every rating separately, while this panel counts each student once by their average — so their percentages differ.',
          },
          {
            title: 'The term matters here',
            body:
              foundationTerm === 1
                ? 'These figures use Term 1 (Entry) ratings. Overall and Term 2 show Term 2 (Exit) ratings, as the Stage Selection card does.'
                : 'These figures use Term 2 (Exit) ratings. On the Foundation dashboard, choosing Term 1 switches them to Term 1 (Entry) ratings.',
          },
          {
            title: 'Each student is counted once',
            body: 'Each student has one Holistic Progress Card record per session, so these are student counts, not counts of ratings.',
          },
        ],
      };
    }
    case 'preparatory':
      // Preparatory services run with applyYearFilter: false; most Class III–V rows of the
      // 2025-26 load carry createdAt 0000-00-00, which a session filter would drop.
      return marksStage(
        'Preparatory Stage',
        'Class III–V',
        'Every Class III–V student with a final result on record.',
        [
          {
            title: 'Includes results without an upload date',
            body: 'Many Class III–V results were loaded without an upload date. So that no student is left out, this stage counts every result on record instead of filtering by session, which means choosing another session does not change these figures. Each student appears only once and the total matches the full Class III–V enrolment, so these are most likely the current session’s results.',
            tone: 'caution',
          },
        ],
      );
    case 'middle':
      return marksStage('Middle Stage', 'Class VI–VIII', 'Class VI–VIII students with a final result recorded for the selected session.');
    case 'secondary':
      return marksStage(
        'Secondary Stage',
        'Class IX and XI',
        'Class IX and XI students with a final result recorded for the selected session. Class X is not included.',
      );
  }
};

/**
 * "How it's calculated" for a Students by Score Band panel: the bands with this panel's
 * own counts, then how the stage scores a student and whom it counts. Rendered in a
 * portal so no card's overflow or transform can clip it. Esc, the backdrop, × and
 * "Got it" close it; focus stays inside while open and returns to the trigger after.
 */
export const ScoreBandInfoDialog: React.FC<{
  stage: ScoreBandStage;
  foundationTerm?: 1 | 2;
  bands: BandRow[];
  totalStudents: number;
  onClose: () => void;
}> = ({ stage, foundationTerm = 2, bands, totalStudents, onClose }) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Latest onClose without re-running the effect (and re-grabbing focus) on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const text = stageText(stage, foundationTerm);

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>('button, [tabindex="0"]');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      trigger?.focus();
    };
  }, []);

  const sectionTitle = 'text-[11px] font-bold uppercase tracking-wide text-indigo-700 mb-1.5';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex w-full max-w-xl max-h-[85vh] flex-col rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-base font-bold text-gray-900">
              How “Students by Score Band” is worked out
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">{text.name} · {text.classes}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* tabIndex so keyboard users can scroll the text when it is longer than the screen */}
        <div className="space-y-5 overflow-y-auto px-5 py-4 text-[13px] leading-relaxed text-gray-700" tabIndex={0}>
          <section>
            <h3 className={sectionTitle}>The three bands</h3>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-1 font-semibold">Band</th>
                  <th className="py-1 font-semibold">Score</th>
                  <th className="py-1 text-right font-semibold">Students</th>
                  <th className="py-1 text-right font-semibold">Share</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b) => (
                  <tr key={b.key} className="border-t border-gray-100">
                    <td className="py-1.5 font-semibold text-gray-800">
                      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${b.color}`} aria-hidden="true" />
                      {b.label}
                    </td>
                    <td className="py-1.5">{b.range.replace(/^score /, '')}</td>
                    <td className="py-1.5 text-right tabular-nums">{b.count.toLocaleString()}</td>
                    <td className="py-1.5 text-right tabular-nums">{b.pct.toFixed(1)}%</td>
                  </tr>
                ))}
                <tr className="border-t border-gray-200 font-semibold text-gray-800">
                  <td className="py-1.5" colSpan={2}>All students counted</td>
                  <td className="py-1.5 text-right tabular-nums">{totalStudents.toLocaleString()}</td>
                  <td className="py-1.5 text-right tabular-nums">100%</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-xs text-gray-500">
              Each student is counted once, in exactly one band, so the shares add up to 100%. A score of exactly 50% or
              90% counts as Moderate.
            </p>
          </section>

          <section>
            <h3 className={sectionTitle}>How a student’s score is worked out</h3>
            <p>{text.score}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {text.examples.map((example) => (
                <span key={example} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {example}
                </span>
              ))}
            </div>
          </section>

          <section>
            <h3 className={sectionTitle}>Who is counted</h3>
            <p>{text.counted}</p>
          </section>

          <section>
            <h3 className={sectionTitle}>Worth knowing</h3>
            <div className="space-y-2">
              {text.notes.map((note) => (
                <div
                  key={note.title}
                  className={`rounded-lg border-l-4 px-3 py-2 ${
                    note.tone === 'caution' ? 'border-amber-400 bg-amber-50' : 'border-indigo-300 bg-indigo-50/60'
                  }`}
                >
                  <p className={`text-[13px] font-semibold ${note.tone === 'caution' ? 'text-amber-900' : 'text-gray-900'}`}>
                    {note.title}
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-700">{note.body}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="flex justify-end border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
